// PWA Update Notification Component

import React from 'react'
import { useServiceWorker } from '../../hooks/usePWA'
import styles from './PWAUpdateNotification.module.css'

interface PWAUpdateNotificationProps {
  onDismiss?: () => void
  autoShow?: boolean
}

export const PWAUpdateNotification: React.FC<PWAUpdateNotificationProps> = ({
  onDismiss,
  autoShow = true
}) => {
  const { hasUpdate, isUpdating, activateUpdate } = useServiceWorker()

  if (!autoShow || !hasUpdate) {
    return null
  }

  const handleUpdate = async () => {
    await activateUpdate()
    if (onDismiss) {
      onDismiss()
    }
  }

  const handleDismiss = () => {
    if (onDismiss) {
      onDismiss()
    }
  }

  return (
    <div className={styles.notification}>
      <div className={styles.content}>
        <div className={styles.icon}>🆕</div>
        <div className={styles.text}>
          <div className={styles.title}>Update Available</div>
          <div className={styles.message}>
            A new version of Clokd is ready to install
          </div>
        </div>
      </div>
      
      <div className={styles.actions}>
        <button
          className={styles.dismissButton}
          onClick={handleDismiss}
          disabled={isUpdating}
        >
          Later
        </button>
        <button
          className={styles.updateButton}
          onClick={handleUpdate}
          disabled={isUpdating}
        >
          {isUpdating ? (
            <>
              <span className={styles.spinner}></span>
              Updating...
            </>
          ) : (
            <>
              <span className={styles.updateIcon}>⬇️</span>
              Update Now
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// Separate component for manual update checking
export const PWAUpdateChecker: React.FC = () => {
  const { checkForUpdates, hasUpdate, isUpdating, activateUpdate } = useServiceWorker()

  const handleCheckUpdates = async () => {
    const updateFound = await checkForUpdates()
    if (!updateFound) {
      // Could show a toast notification here
      console.log('No updates available')
    }
  }

  const handleUpdate = async () => {
    await activateUpdate()
  }

  if (hasUpdate) {
    return (
      <div className={styles.updateChecker}>
        <div className={styles.updateAvailable}>
          <span className={styles.updateIndicator}>🔄</span>
          <span className={styles.updateText}>Update available</span>
          <button
            className={styles.installUpdateButton}
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Install'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.updateChecker}>
      <button
        className={styles.checkButton}
        onClick={handleCheckUpdates}
        title="Check for updates"
      >
        <span className={styles.checkIcon}>🔄</span>
        Check for Updates
      </button>
    </div>
  )
}